import React, { useState } from 'react';
import { Button } from 'primereact/button';
import './HelpPage.css';

function HelpPage() {
    const [activeSection, setActiveSection] = useState('overview');
    const [expandedFaq, setExpandedFaq] = useState(null);

    const sections = [
        { id: 'overview', label: 'Overview', icon: 'pi-info-circle' },
        { id: 'roles', label: 'User Roles', icon: 'pi-users' },
        { id: 'data-entry', label: 'Data Entry', icon: 'pi-pencil' },
        { id: 'verification', label: 'Verification', icon: 'pi-check-circle' },
        { id: 'admin', label: 'Administration', icon: 'pi-cog' },
        { id: 'reports', label: 'Reports', icon: 'pi-chart-bar' },
        { id: 'faq', label: 'FAQ', icon: 'pi-question' },
    ];

    const faqs = [
        {
            q: "I can't see the 'Adjust Attendance' card",
            a: "This card is visible to all admins in the Task Center. Login to Admin Panel and navigate to Task Center."
        },
        {
            q: "My entry doesn't appear in the verification list",
            a: "Click the Refresh button in the Verify Entries panel. If still missing, the entry may have already been processed."
        },
        {
            q: "Can I edit an entry after submitting?",
            a: "No, once submitted you cannot edit. If incorrect, ask the Verifier to reject it, then create a new entry."
        },
        {
            q: "What happens when an entry is rejected?",
            a: "The entry is deleted from the system. The rejection is logged in the audit trail for accountability."
        },
        {
            q: "Can I assign the same person for both roles?",
            a: "No, the Data Entry Person and Verifier must be different individuals to ensure proper checks and balances."
        },
        {
            q: "How do I print this manual?",
            a: "Click the 'Print Manual' button at the top right. Use Ctrl+P or Cmd+P to print or save as PDF."
        }
    ];

    const handlePrint = () => {
        window.print();
    };

    const renderContent = () => {
        switch (activeSection) {
            case 'overview':
                return (
                    <div className="help-section">
                        <h2><i className="pi pi-info-circle"></i> Overview</h2>
                        <div className="content-card highlight">
                            <h3>What is Attendance Data Management?</h3>
                            <p>
                                The Attendance Data Management module allows authorized personnel to manually enter 
                                or adjust teacher attendance records when the fingerprint machine punch is deleted, 
                                not saved, or missing.
                            </p>
                        </div>
                        
                        <h3>Key Features</h3>
                        <div className="feature-grid">
                            <div className="feature-card">
                                <div className="feature-icon" style={{ background: '#e0f2fe' }}>
                                    <i className="pi pi-pencil" style={{ color: '#0284c7' }}></i>
                                </div>
                                <h4>Manual Entry</h4>
                                <p>Enter missing In/Out times with remarks</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon" style={{ background: '#dcfce7' }}>
                                    <i className="pi pi-check-circle" style={{ color: '#16a34a' }}></i>
                                </div>
                                <h4>Two-Person Verification</h4>
                                <p>Maker-Checker workflow for data integrity</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon" style={{ background: '#fef3c7' }}>
                                    <i className="pi pi-history" style={{ color: '#d97706' }}></i>
                                </div>
                                <h4>Audit Trail</h4>
                                <p>Complete logging of all changes</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon" style={{ background: '#f3e8ff' }}>
                                    <i className="pi pi-eye" style={{ color: '#9333ea' }}></i>
                                </div>
                                <h4>Visual Indicators</h4>
                                <p>"To be Approved" badges in reports</p>
                            </div>
                        </div>

                        <h3>Process Flow</h3>
                        <div className="process-flow">
                            <div className="flow-step">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Identify Missing Data</strong>
                                    <p>Fingerprint punch deleted or not saved</p>
                                </div>
                            </div>
                            <div className="flow-arrow"><i className="pi pi-arrow-down"></i></div>
                            <div className="flow-step">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Data Entry Person</strong>
                                    <p>Enters In/Out Time with remarks</p>
                                </div>
                            </div>
                            <div className="flow-arrow"><i className="pi pi-arrow-down"></i></div>
                            <div className="flow-step pending">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Pending Status</strong>
                                    <p>Shows "To be Approved" in reports</p>
                                </div>
                            </div>
                            <div className="flow-arrow"><i className="pi pi-arrow-down"></i></div>
                            <div className="flow-step success">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Verifier Approves</strong>
                                    <p>Entry becomes verified and active</p>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'roles':
                return (
                    <div className="help-section">
                        <h2><i className="pi pi-users"></i> User Roles</h2>
                        
                        <div className="roles-grid">
                            <div className="role-card data-entry">
                                <div className="role-header">
                                    <i className="pi pi-pencil"></i>
                                    <h3>Data Entry Person</h3>
                                </div>
                                <div className="role-body">
                                    <p className="role-desc">Enters or adjusts missing attendance records</p>
                                    <h4>Responsibilities:</h4>
                                    <ul>
                                        <li>Search and select teacher</li>
                                        <li>Enter missing In/Out times</li>
                                        <li>Provide remarks/reason</li>
                                        <li>Submit for verification</li>
                                    </ul>
                                    <div className="role-note">
                                        <i className="pi pi-info-circle"></i>
                                        Cannot verify own entries
                                    </div>
                                </div>
                            </div>

                            <div className="role-card verifier">
                                <div className="role-header">
                                    <i className="pi pi-check-circle"></i>
                                    <h3>Verifier (Supervisor)</h3>
                                </div>
                                <div className="role-body">
                                    <p className="role-desc">Approves or rejects manual entries</p>
                                    <h4>Responsibilities:</h4>
                                    <ul>
                                        <li>Review pending entries</li>
                                        <li>Verify with teacher if needed</li>
                                        <li>Approve valid entries</li>
                                        <li>Reject incorrect entries</li>
                                    </ul>
                                    <div className="role-note">
                                        <i className="pi pi-info-circle"></i>
                                        Must be different from Data Entry person
                                    </div>
                                </div>
                            </div>

                            <div className="role-card admin">
                                <div className="role-header">
                                    <i className="pi pi-cog"></i>
                                    <h3>Administrator</h3>
                                </div>
                                <div className="role-body">
                                    <p className="role-desc">Assigns Data Entry and Verifier roles</p>
                                    <h4>Responsibilities:</h4>
                                    <ul>
                                        <li>Access Settings page</li>
                                        <li>Assign Data Entry person</li>
                                        <li>Assign Verifier person</li>
                                        <li>Manage role changes</li>
                                    </ul>
                                    <div className="role-note">
                                        <i className="pi pi-info-circle"></i>
                                        Can select from teachers or staff
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'data-entry':
                return (
                    <div className="help-section">
                        <h2><i className="pi pi-pencil"></i> Data Entry Guide</h2>
                        
                        <div className="step-guide">
                            <h3>How to Enter Missing Attendance</h3>
                            
                            <div className="guide-step">
                                <div className="guide-step-header">
                                    <span className="step-badge">Step 1</span>
                                    <h4>Access the Module</h4>
                                </div>
                                <div className="guide-step-body">
                                    <ol>
                                        <li>Login to <strong>Admin Panel</strong> (/admin)</li>
                                        <li>Navigate to <strong>Task Center</strong></li>
                                        <li>Click <strong>"Adjust Attendance"</strong> card</li>
                                    </ol>
                                </div>
                            </div>

                            <div className="guide-step">
                                <div className="guide-step-header">
                                    <span className="step-badge">Step 2</span>
                                    <h4>Search for Teacher</h4>
                                </div>
                                <div className="guide-step-body">
                                    <ol>
                                        <li>Type teacher's <strong>name, TID, or NIC</strong> in search box</li>
                                        <li>Select the correct teacher from dropdown</li>
                                        <li>Confirm selection (displayed in blue box)</li>
                                    </ol>
                                </div>
                            </div>

                            <div className="guide-step">
                                <div className="guide-step-header">
                                    <span className="step-badge">Step 3</span>
                                    <h4>Select Date</h4>
                                </div>
                                <div className="guide-step-body">
                                    <ol>
                                        <li>Click the <strong>calendar icon</strong></li>
                                        <li>Select the date for attendance adjustment</li>
                                        <li><em>Note: Future dates cannot be selected</em></li>
                                    </ol>
                                </div>
                            </div>

                            <div className="guide-step">
                                <div className="guide-step-header">
                                    <span className="step-badge">Step 4</span>
                                    <h4>Enter Time Details</h4>
                                </div>
                                <div className="guide-step-body">
                                    <div className="time-format-box">
                                        <p><strong>In Time:</strong> Arrival time (e.g., 08:30)</p>
                                        <p><strong>Out Time:</strong> Departure time (e.g., 16:30)</p>
                                        <p className="hint">At least one time must be entered</p>
                                    </div>
                                </div>
                            </div>

                            <div className="guide-step">
                                <div className="guide-step-header">
                                    <span className="step-badge">Step 5</span>
                                    <h4>Add Remarks & Save</h4>
                                </div>
                                <div className="guide-step-body">
                                    <ol>
                                        <li>Enter reason for manual entry</li>
                                        <li>Examples: "Machine malfunction", "Punch not recorded"</li>
                                        <li>Click <strong>"Save Entry"</strong> button</li>
                                    </ol>
                                    <div className="success-box">
                                        <i className="pi pi-check"></i>
                                        Entry saved with status: <strong>Pending Verification</strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="warning-box">
                            <h4><i className="pi pi-exclamation-triangle"></i> Important Notes</h4>
                            <ul>
                                <li>Manual entries show <strong>"To be Approved"</strong> badge until verified</li>
                                <li>You <strong>cannot verify</strong> your own entries</li>
                                <li>All entries are <strong>logged</strong> with your username and timestamp</li>
                            </ul>
                        </div>
                    </div>
                );

            case 'verification':
                return (
                    <div className="help-section">
                        <h2><i className="pi pi-check-circle"></i> Verification Guide</h2>
                        
                        <div className="step-guide">
                            <h3>How to Verify Entries</h3>
                            
                            <div className="guide-step">
                                <div className="guide-step-header">
                                    <span className="step-badge">Step 1</span>
                                    <h4>Access the Module</h4>
                                </div>
                                <div className="guide-step-body">
                                    <ol>
                                        <li>Login to <strong>Admin Panel</strong> (/admin)</li>
                                        <li>Navigate to <strong>Task Center</strong></li>
                                        <li>Click <strong>"Verify Entries"</strong> card</li>
                                    </ol>
                                </div>
                            </div>

                            <div className="guide-step">
                                <div className="guide-step-header">
                                    <span className="step-badge">Step 2</span>
                                    <h4>Review Pending Entries</h4>
                                </div>
                                <div className="guide-step-body">
                                    <p>Each entry shows:</p>
                                    <table className="info-table">
                                        <tbody>
                                            <tr><td><strong>TID</strong></td><td>Teacher ID</td></tr>
                                            <tr><td><strong>Teacher Name</strong></td><td>Full name</td></tr>
                                            <tr><td><strong>Date</strong></td><td>Attendance date</td></tr>
                                            <tr><td><strong>In/Out Time</strong></td><td>Entered times</td></tr>
                                            <tr><td><strong>Entered By</strong></td><td>Data Entry person</td></tr>
                                            <tr><td><strong>Remarks</strong></td><td>Reason for entry</td></tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="guide-step">
                                <div className="guide-step-header">
                                    <span className="step-badge">Step 3</span>
                                    <h4>Take Action</h4>
                                </div>
                                <div className="guide-step-body">
                                    <div className="action-buttons-demo">
                                        <div className="action-demo approve">
                                            <span className="demo-btn green"><i className="pi pi-check"></i></span>
                                            <div>
                                                <strong>Approve</strong>
                                                <p>Entry becomes verified and active</p>
                                            </div>
                                        </div>
                                        <div className="action-demo reject">
                                            <span className="demo-btn red"><i className="pi pi-times"></i></span>
                                            <div>
                                                <strong>Reject</strong>
                                                <p>Entry is deleted (logged in audit)</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="warning-box">
                            <h4><i className="pi pi-exclamation-triangle"></i> Important Notes</h4>
                            <ul>
                                <li>Always <strong>verify entries</strong> before approving</li>
                                <li>Rejected entries <strong>cannot be recovered</strong> (but are logged)</li>
                                <li>Click <strong>Refresh</strong> to see latest pending entries</li>
                                <li>Approved entries take effect <strong>immediately</strong> in reports</li>
                            </ul>
                        </div>
                    </div>
                );

            case 'admin':
                return (
                    <div className="help-section">
                        <h2><i className="pi pi-cog"></i> Administration Guide</h2>
                        
                        <div className="step-guide">
                            <h3>How to Assign Roles</h3>
                            
                            <div className="guide-step">
                                <div className="guide-step-header">
                                    <span className="step-badge">Step 1</span>
                                    <h4>Access Settings</h4>
                                </div>
                                <div className="guide-step-body">
                                    <ol>
                                        <li>Login to <strong>Admin Panel</strong> (/admin)</li>
                                        <li>Click <strong>Settings</strong> in sidebar</li>
                                        <li>Click <strong>"Attendance Data Management"</strong> card</li>
                                    </ol>
                                </div>
                            </div>

                            <div className="guide-step">
                                <div className="guide-step-header">
                                    <span className="step-badge">Step 2</span>
                                    <h4>View Current Assignments</h4>
                                </div>
                                <div className="guide-step-body">
                                    <p>Current assignments are shown in green labels:</p>
                                    <div className="demo-label green">
                                        Current: <strong>Geethika</strong> (geethika)
                                    </div>
                                </div>
                            </div>

                            <div className="guide-step">
                                <div className="guide-step-header">
                                    <span className="step-badge">Step 3</span>
                                    <h4>Change Assignment</h4>
                                </div>
                                <div className="guide-step-body">
                                    <ol>
                                        <li>Search by <strong>name, TID, or NIC</strong></li>
                                        <li>Select from dropdown (teachers + staff)</li>
                                        <li>New selection appears in yellow label</li>
                                    </ol>
                                    <div className="demo-label yellow">
                                        New: <strong>Ranjani De Silva</strong> (708610879V)
                                    </div>
                                </div>
                            </div>

                            <div className="guide-step">
                                <div className="guide-step-header">
                                    <span className="step-badge">Step 4</span>
                                    <h4>Save Changes</h4>
                                </div>
                                <div className="guide-step-body">
                                    <p>Click <strong>"Save Assignments"</strong> button</p>
                                    <p>Changes take effect <strong>immediately</strong></p>
                                </div>
                            </div>
                        </div>

                        <div className="warning-box">
                            <h4><i className="pi pi-exclamation-triangle"></i> Important Notes</h4>
                            <ul>
                                <li>Data Entry Person and Verifier must be <strong>different</strong> people</li>
                                <li>Both <strong>teachers and staff</strong> can be assigned</li>
                                <li>Users must have an <strong>active login account</strong></li>
                            </ul>
                        </div>
                    </div>
                );

            case 'reports':
                return (
                    <div className="help-section">
                        <h2><i className="pi pi-chart-bar"></i> Reports Guide</h2>
                        
                        <h3>Identifying Manual Entries</h3>
                        <p>In attendance reports, manual entries pending verification show a special badge:</p>
                        
                        <div className="report-demo">
                            <table className="demo-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Status</th>
                                        <th>In Time</th>
                                        <th>Out Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Jan 20, 2026</td>
                                        <td><span className="status-badge present">Present</span></td>
                                        <td>08:30</td>
                                        <td>16:30</td>
                                    </tr>
                                    <tr className="highlight-row">
                                        <td>Jan 21, 2026</td>
                                        <td>
                                            <span className="status-badge present">Present</span>
                                            <span className="status-badge pending">To be Approved</span>
                                        </td>
                                        <td>08:45</td>
                                        <td>-</td>
                                    </tr>
                                    <tr>
                                        <td>Jan 22, 2026</td>
                                        <td><span className="status-badge present">Present</span></td>
                                        <td>08:25</td>
                                        <td>16:45</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <h3>After Verification</h3>
                        <p>Once verified, the entry appears normally without any badge:</p>
                        
                        <div className="report-demo">
                            <table className="demo-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Status</th>
                                        <th>In Time</th>
                                        <th>Out Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Jan 21, 2026</td>
                                        <td><span className="status-badge present">Present</span></td>
                                        <td>08:45</td>
                                        <td>16:30</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <h3>Audit Trail</h3>
                        <p>All changes are logged with:</p>
                        <table className="info-table">
                            <tbody>
                                <tr><td><strong>Date/Time</strong></td><td>When the action occurred</td></tr>
                                <tr><td><strong>Action</strong></td><td>in_time, out_time, is_verified</td></tr>
                                <tr><td><strong>Old Value</strong></td><td>Previous value (if any)</td></tr>
                                <tr><td><strong>New Value</strong></td><td>New value entered</td></tr>
                                <tr><td><strong>User</strong></td><td>Who performed the action</td></tr>
                                <tr><td><strong>Remarks</strong></td><td>Reason for change</td></tr>
                            </tbody>
                        </table>
                    </div>
                );

            case 'faq':
                return (
                    <div className="help-section">
                        <h2><i className="pi pi-question"></i> Frequently Asked Questions</h2>
                        
                        <div className="faq-list">
                            {faqs.map((faq, index) => (
                                <div 
                                    key={index} 
                                    className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
                                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                                >
                                    <div className="faq-question">
                                        <span>{faq.q}</span>
                                        <i className={`pi ${expandedFaq === index ? 'pi-minus' : 'pi-plus'}`}></i>
                                    </div>
                                    {expandedFaq === index && (
                                        <div className="faq-answer">
                                            {faq.a}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="contact-box">
                            <h3><i className="pi pi-envelope"></i> Need More Help?</h3>
                            <p>Contact the System Administrator for additional assistance.</p>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="help-page">
            <div className="help-header">
                <div className="help-title">
                    <i className="pi pi-book"></i>
                    <div>
                        <h1>User Manual</h1>
                        <p>Attendance Data Management System</p>
                    </div>
                </div>
                <Button 
                    label="Print Manual" 
                    icon="pi pi-print" 
                    className="p-button-outlined print-btn"
                    onClick={handlePrint}
                />
            </div>

            <div className="help-container">
                <nav className="help-nav">
                    {sections.map(section => (
                        <div
                            key={section.id}
                            className={`nav-item ${activeSection === section.id ? 'active' : ''}`}
                            onClick={() => setActiveSection(section.id)}
                        >
                            <i className={`pi ${section.icon}`}></i>
                            <span>{section.label}</span>
                        </div>
                    ))}
                </nav>

                <main className="help-content">
                    {renderContent()}
                </main>
            </div>
        </div>
    );
}

export default HelpPage;
